import LevelChip from '@app/src/components/LevelChip'
import type { Provider } from '@app/src/types/organizations'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { Button, Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { findBaselineLevel } from '../bulkCreateExpectationLogic'

const PAGE_SIZE = 50

export type ProviderListProps = {
  providers: Provider[]
  templateId: number | null
  assessmentTemplate?: AssessmentTemplate
  selectable?: {
    selectedIds: number[]
    onToggle: (id: number) => void
  }
  showLevel?: boolean
}

const ProviderList: React.FC<ProviderListProps> = ({
  providers,
  templateId,
  assessmentTemplate,
  selectable,
  showLevel = true,
}) => {
  const { formatMessage } = useIntl()
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const visibleProviders = providers.slice(0, visibleCount)
  const hasMore = visibleCount < providers.length

  return (
    <Stack>
      <List disablePadding sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
        {visibleProviders.map((provider, index) => {
          const level = showLevel ? findBaselineLevel(provider, templateId) : undefined
          const isSelected = selectable?.selectedIds.includes(provider.id) ?? false

          return (
            <ListItem
              key={provider.id}
              divider={index < visibleProviders.length - 1}
              secondaryAction={
                level != null ? <LevelChip level={level} assessmentTemplate={assessmentTemplate} /> : null
              }
              disablePadding
            >
              {selectable ? (
                <ListItemButton onClick={() => selectable.onToggle(provider.id)} dense sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 56, paddingRight: 2, paddingLeft: 1 }}>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        'aria-label': formatMessage({
                          id: 'bulkCreateExpectation.steps.selectedCompanies.removeAriaLabel',
                        }),
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={provider.name} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              ) : (
                <ListItemText
                  primary={provider.name}
                  sx={{ px: 4, py: 1 }}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              )}
            </ListItem>
          )
        })}
      </List>

      {hasMore && (
        <Button
          variant="outlined"
          onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
          sx={{ alignSelf: 'center', my: 1 }}
        >
          {formatMessage({ id: 'general.showMore' })}
        </Button>
      )}
    </Stack>
  )
}

export default ProviderList
